import React, { useState, useEffect} from 'react';

import { styled} from '@mui/material/styles';
import { Grid, Link, Stack, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Chip, ToggleButtonGroup, ToggleButton, FormControl, OutlinedInput, InputAdornment} from '@mui/material';
import { Search } from '@mui/icons-material';

import DocumentViewer from './DocumentViewer';
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import FilerFine from '../dataObjects/FilerFine';
import { WaiverStatusTypeEnums } from '../dataObjects/WaiverStatusTypeEnums';

interface WaiverListProps {
    pendingWaivers?: FilerFine[];
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: '#fafafa',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const FilerWaiverList = (props: WaiverListProps): JSX.Element => {

    const Headers = ['Filer', 'Audit Period', 'Days Late', 'Penalty', 'Waivers', 'Director Decision'];

    const [isDocumentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [waiverStatusFilter, setWaiverStatusFilter] = useState('pending');
    const [pendingWaivers, setPendingWaivers] = useState<FilerFine[]>();
    const [currentWaiver, setCurrentWaiver] = useState(null);
    const [currentWaiverUrl, setCurrentWaiverUrl] = useState('');

    useEffect(() => {
        if (props.pendingWaivers) {
            setPendingWaivers(props.pendingWaivers);
        }
    }, [])

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const switchWaiverStatus = (event: any, newStatus: string) => {
        if (newStatus !== null) {
            setWaiverStatusFilter(newStatus);
        };
        getPendingWaivers(newStatus === 'all');
    };

    const getPendingWaivers = async (includeAll: boolean = false) => {
        await fetch(`/FineTracker/GetPendingWaivers?includeAll=${includeAll}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetPendingWaivers',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let waivers = result.result;
                    setPendingWaivers(waivers);
                }
            }
        )
    }

    const openDocumentViewer = (waiver: any, url: string) => {
        setDocumentViewerOpen(true);
        setCurrentWaiver(waiver);
        setCurrentWaiverUrl(url);
    }

    const closeDocumentViewer = (reload: boolean = false) => {
        setDocumentViewerOpen(false);
        setCurrentWaiver(null);
        setCurrentWaiverUrl('');
        if (reload) {
            getPendingWaivers();
        }
    };

  
    return (
        <Grid container marginTop={5} display={'flex'} justifyContent="center">
            <Grid item xs={2} marginBottom={2}>
                <FormControl sx={{ minWidth: 200 }} variant="outlined" size="small">
                    <OutlinedInput
                        aria-label='search by filer name'
                        title='search by filer name'
                        placeholder={"Search filer..."}
                        startAdornment={
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        }
                    />
                </FormControl>
            </Grid>
            <Grid item xs={7} marginBottom={2}>
                <Stack direction="row" spacing={1} alignItems="center" display="flex" justifyContent="flex-start">
                    <Typography variant="subtitle1" fontWeight={550}>
                        Filter waivers by:
                    </Typography>
                    <ToggleButtonGroup
                        color="primary"
                        value={waiverStatusFilter}
                        fullWidth={true}
                        size="small"
                        exclusive
                        aria-label="Waiver Status"
                        sx={{ width: 200 }}
                        onChange={switchWaiverStatus}
                    >
                        <ToggleButton value="pending" sx={{ fontWeight: 800 }}>Pending</ToggleButton>
                        <ToggleButton value="all" sx={{ fontWeight: 800 }}>All</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
            </Grid>
            <Grid item xs={10.5} marginTop={3}>
                <TableContainer>
                    <Table aria-label="Filer Fine Table" size="small">
                        <TableHead>
                            <TableRow>
                                {
                                    Headers.map((header, index) => (
                                        <TableCell key={index}>
                                            <Typography variant={'subtitle2'} fontWeight={600}>
                                                {header}
                                            </Typography>
                                        </TableCell>
                                    ))
                                }
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pendingWaivers?.map((waiver: FilerFine) => (
                                <StyledTableRow key={waiver.cpfId}>
                                    <TableCell sx={{ width: "30%" }}>
                                        <Typography variant={'body2'}>
                                            <Link href={`/Filers?q=${waiver.cpfId}`} target="_blank">{waiver.cpfId}</Link> {waiver.fullName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={'body2'}>
                                            {waiver.auditPeriodId}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={'body2'}>
                                            {waiver.daysLate}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant={'body2'} fontWeight={550} color="error">
                                            {
                                                waiver.reducedFineAmount ? `${waiver.reducedFineAmount}(reduced from ${waiver.unmodifiedFineAmount})` : waiver.modifiedFineAmount
                                            }
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {
                                            waiver.waiverStatus === WaiverStatusTypeEnums.Processed ?
                                                <Chip label={`Processed`} color="success" variant="outlined" onClick={() => openDocumentViewer(waiver, '')}></Chip>
                                                :
                                                <Chip label={`Pending`} color="warning" variant="outlined" onClick={() => openDocumentViewer(waiver, '')}></Chip>
                                        }
                                    </TableCell>
                                    <TableCell>
                                        {waiver.waiverDecisionTypeDescription}
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            <DocumentViewer isDocumentViewerOpen={isDocumentViewerOpen} closeDocumentViewer={closeDocumentViewer} currentWaiverInfo={currentWaiver} currentNoticeUrl={currentWaiverUrl}></DocumentViewer>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </Grid>
    );
};

export default FilerWaiverList;